import {useEffect, useRef, useState} from 'react'
import {useRouter} from 'next/router'
import _ from 'lodash'
// Contexts
import {useCombinedAuthStatus} from '../../../hooks/use-combined-auth-status'
// Hooks
import {useRouteParams} from '../../../hooks/use-route-params'
// Components
import EntryImagesModal from '../../modals/entry-images-modal'
import PhotoScrollEntry from './photo-scroll-entry'
import PhotoGallerySubmissionWizard from '../photo-gallery-submission-wizard'
import PhotoGalleryTopBar from '../photo-gallery-top-bar'
import PhotoGalleryAd from '../photo-gallery-ad'
// Utils
import {accessBallotDisplayType} from '../../../utils/ballot-access'
import {addQueryParam, removeQueryParamByName} from '../../../utils/router-helpers'
// Types
import type {SyntheticEvent} from 'react'
import type {GalleryEntry, PreviousVote, PhotoGalleryContainerProps} from '../../../types/photo-gallery'
// Styles
import styles from './photo-scroll-gallery.module.scss'

const PhotoScrollGallery = ({
  customer,
  ballot,
  displayAd,
  currentQuestion,
  currentGroup,
  allFinalists,
  rankDictionary,
  currentWindowHeight,
  currentWindowWidth,
  canSubmit,
  submissionIsProcessing,
  hasSponsorshipAd,
  sendModalIsIn = null,
  onPaginate,
  onCastVote,
  onCastSubmission,
  onSelectedAnswerChange,
}: PhotoGalleryContainerProps) => {
  const {voter} = useCombinedAuthStatus()

  const router = useRouter()
  const {isReady, entryId} = useRouteParams()

  // From currentQuestion
  const [previousVote, setPreviousVote] = useState<PreviousVote | null>(null)
  const [scrollEntries, setScrollEntries] = useState<GalleryEntry[]>([])

  const [modalEntry, setModalEntry] = useState<GalleryEntry | null>(null)

  const ballotDisplayType = accessBallotDisplayType(ballot)
  const hasQuestionSubmissions = _.some(allFinalists, photo => photo.sub_category_id === currentQuestion?._id)
  const shouldCenterEmptySubmissionWizard = (
    ballotDisplayType === 'photo-submissions-submit'
    && !hasQuestionSubmissions
  )

  const [scrollHeight, setScrollHeight] = useState(0)

  const scrollContainerEl = useRef<HTMLDivElement>(null)
  const scrollGallery = (scrollDirection: number) => {
    if (!Number(scrollDirection) || !scrollContainerEl.current) return

    const scrollLeft = scrollContainerEl.current.scrollLeft
    const galleryWidth = scrollContainerEl.current.offsetWidth
    const newScrollLeft = scrollLeft + (scrollDirection * galleryWidth)

    scrollContainerEl.current.scrollTo({left: newScrollLeft, behavior: 'smooth'})
  }

  // Track whether a modal (submission wizard or entry images) is open so we can
  // disable horizontal scrolling on the stage container.  On Android mobile
  // browsers, touch events on position-fixed overlays propagate to scrollable
  // ancestors, making interaction inside the modal extremely difficult.
  const [isModalOpen, setIsModalOpen] = useState(false)
  const handleModalIsIn = (value: boolean) => {
    setIsModalOpen(value)
    sendModalIsIn?.(value)
  }

  const [paymentIsProcessing, setPaymentIsProcessing] = useState(false)
  const sendPaymentIsProcessing = (value: boolean): void => setPaymentIsProcessing(value)

  useEffect(() => {
    let mounted = true
    if (mounted) {
      const topNavClearance = 45
      const scrollTitleClearance = 58
      const bottomNavClearance = hasSponsorshipAd ? 90 : 66
      const rowPadding = 24

      const totalClearance = topNavClearance + scrollTitleClearance + bottomNavClearance + rowPadding

      const insideHeaderFooterHeight = currentWindowHeight - totalClearance
      const newScrollHeight = insideHeaderFooterHeight
      setScrollHeight(newScrollHeight)
    }
    return () => {
      mounted = false
    }
  }, [currentWindowWidth, currentWindowHeight])

  const generateScrollEntries = (allFinalists: GalleryEntry[], subCategoryId: string | null): GalleryEntry[] => {
    const allQuestionPhotos = _.filter(allFinalists, photo => photo.sub_category_id === subCategoryId)
    let scrollEntries = _.cloneDeep(allQuestionPhotos)

    let hasDisplayAd = Boolean(displayAd) && (typeof displayAd === 'object')
    if (hasDisplayAd) {
      hasDisplayAd = (Array.isArray(displayAd) && displayAd.length > 0)
        || (displayAd !== null && Object.keys(displayAd).length > 0)
    }
    if (hasDisplayAd) {
      if (allQuestionPhotos?.length > 2) {
        scrollEntries.splice(2, 0, {
          key: 'ad',
          type: 'ad',
          _id: 'ad',
          additional_form_fields: [],
        })
      } else {
        scrollEntries.push({
          key: 'ad',
          type: 'ad',
          _id: 'ad',
          additional_form_fields: [],
        })
      }
    }
    return scrollEntries
  }

  const [isShowingPreviousVote, setIsShowingPreviousVote] = useState(false)
  useEffect(() => {
    if (!isReady) return
    const getEntryIndexByEntryId = (entries: GalleryEntry[], entryId: string | string[]): number | undefined => {
      if (!entryId) return

      const movedEntryIndex = _.findIndex(entries, entry => entry._id === entryId)
      return movedEntryIndex
    }
    const getEntryIndexByPhotoImage = (entries: GalleryEntry[], photoImage: string | null): number | undefined => {
      if (!photoImage) return

      const movedEntryIndex = _.findIndex(entries, entry => entry.photo_image === photoImage)
      return movedEntryIndex
    }
    const moveEntryToFrontByEntryIndex = (
      entries: GalleryEntry[], movedEntryIndex: number | undefined, isSelected = false,
    ): GalleryEntry[] => {
      let newScrollEntries = _.cloneDeep(entries)
      let pulledEntry: GalleryEntry | null = null
      if (typeof movedEntryIndex === 'number' && movedEntryIndex > -1) {
        [pulledEntry] = _.pullAt(newScrollEntries, movedEntryIndex)
        pulledEntry['isSelected'] = isSelected
        newScrollEntries.unshift(pulledEntry)
      }
      return newScrollEntries
    }

    const previousQuestionVotes = _.filter(voter?.votes, vote => vote.sub_category_id === currentQuestion._id)
    let newSelectedPhotoUrl: string | null = null
    let newPreviousVote: PreviousVote | null = null
    if (previousQuestionVotes.length > 0) {
      const chronologicalPreviousVotes = _.orderBy(previousQuestionVotes, ['voted_time'], ['desc'])
      const mostRecentPreviousVote = _.first(chronologicalPreviousVotes)!
      const currentDateTime = new Date()
      const voteDateTime = new Date(mostRecentPreviousVote.voted_time)
      const currentDateTimeDayArray = [
        currentDateTime.getFullYear(),
        currentDateTime.getMonth() + 1,
        currentDateTime.getDate(),
      ]
      const voteDateTimeDayArray = [
        voteDateTime.getFullYear(),
        voteDateTime.getMonth() + 1,
        voteDateTime.getDate(),
      ]
      const hasVotedToday = _.isEqual(currentDateTimeDayArray, voteDateTimeDayArray)

      const newIsShowingPreviousVote = (
        ballotDisplayType !== 'photo-voting-finalist-announcement'
        && (
          !ballot.general?.one_vote_per_day_turn_on
          || hasVotedToday
        )
      )
      setIsShowingPreviousVote(newIsShowingPreviousVote)

      newSelectedPhotoUrl = newIsShowingPreviousVote ? mostRecentPreviousVote.voted_choice : null

      const newSelectedPhotoName = newIsShowingPreviousVote ? mostRecentPreviousVote.photo_name : null
      const newSelectedPhotoDescription = newIsShowingPreviousVote ? mostRecentPreviousVote.photo_description : null
      newPreviousVote = {
        photo_image: newSelectedPhotoUrl,
        photo_name: newSelectedPhotoName ?? null,
        photo_description: newSelectedPhotoDescription ?? null,
      }
    }
    setPreviousVote(newPreviousVote)

    const allScrollEntries = generateScrollEntries(allFinalists, currentQuestion._id)
    const filteredScrollEntries = _.filter(allScrollEntries, Boolean)
    let newScrollEntries = _.cloneDeep(filteredScrollEntries)
    if (entryId) {
      const entryIndex = getEntryIndexByEntryId(newScrollEntries, entryId)
      const movedEntry = entryIndex !== undefined ? filteredScrollEntries[entryIndex] : undefined
      if (movedEntry) {
        openEntryImagesModal(movedEntry)
      }
      newScrollEntries = moveEntryToFrontByEntryIndex(newScrollEntries, entryIndex)
    }
    if (newPreviousVote) {
      const entryIndex = getEntryIndexByPhotoImage(newScrollEntries, newPreviousVote?.photo_image)
      const isSelected = true
      newScrollEntries = moveEntryToFrontByEntryIndex(newScrollEntries, entryIndex, isSelected)
    }

    if (ballotDisplayType === 'photo-voting-results') {
      newScrollEntries = newScrollEntries.sort((a, b) => {
        if ((!a.rank || a.rank === 0) && (!b.rank || b.rank === 0)) return 0
        if (!a.rank || a.rank === 0) return 1
        if (!b.rank || b.rank === 0) return -1
        return a.rank - b.rank
      })
    }

    setScrollEntries(newScrollEntries)
  }, [
    currentQuestion,
    isReady,
    entryId,
  ])

  const openEntryImagesModal = (entry: GalleryEntry) => {
    addQueryParam(router, {entry_id: entry._id})
    setModalEntry(entry)
    handleModalIsIn(true)
  }

  const onCloseEntryImagesModal = () => {
    removeQueryParamByName(router, 'entry_id')

    setModalEntry(null)
    handleModalIsIn(false)
  }

  const handleSelectedAnswerChange = (syntheticEvent: SyntheticEvent, scrollEntry: GalleryEntry) => {
    let newScrollEntries = _.map(_.cloneDeep(scrollEntries), entry => ({...entry, isSelected: false}))
    const selectedEntryIndex = _.findIndex(newScrollEntries, entry => entry.photo_image === scrollEntry.photo_image)
    setIsShowingPreviousVote(selectedEntryIndex >= 0)
    newScrollEntries[selectedEntryIndex] = {...scrollEntry, isSelected: true}
    setScrollEntries(newScrollEntries)
    onSelectedAnswerChange(syntheticEvent, scrollEntry)
  }

  return currentQuestion
    ? (
      <div className={styles['__container']}>
        <div className={styles['photo-scroll-gallery']}>
          <PhotoGalleryTopBar
            variant='scroll'
            hasItems={scrollEntries.length > 0}
            currentQuestion={currentQuestion}
            onScroll={scrollGallery}
          />
          <div
            ref={scrollContainerEl}
            className={`${styles['__stage']} ${isModalOpen ? styles['--modal-open'] : ''}`}
          >
            <div
              className={`${styles['__stage-row']} ${shouldCenterEmptySubmissionWizard ? styles['--center-empty-submission'] : ''}`}
              style={{height: `${scrollHeight}px`}}
            >
              {ballotDisplayType === 'photo-submissions-submit' &&
                <PhotoGallerySubmissionWizard
                  variant='scroll'
                  isCentered={shouldCenterEmptySubmissionWizard}
                  customer={customer}
                  ballot={ballot}
                  question={currentQuestion}
                  previousVote={previousVote}
                  canSubmit={canSubmit}
                  submissionIsProcessing={submissionIsProcessing}
                  paymentIsProcessing={paymentIsProcessing}
                  sendPaymentIsProcessing={sendPaymentIsProcessing}
                  onCastSubmission={onCastSubmission}
                  onPaginate={onPaginate}
                  sendModalIsIn={handleModalIsIn}
                />
              }
              {_.map(scrollEntries, scrollEntry => {
                if (scrollEntry.type === 'option') {
                  return <PhotoScrollEntry
                    key={scrollEntry.key}
                    ballot={ballot}
                    question={currentQuestion}
                    entry={scrollEntry}
                    rankDictionary={rankDictionary}
                    previousVote={previousVote}
                    ballotDisplayType={ballotDisplayType}
                    currentWindowWidth={currentWindowWidth}
                    scrollHeight={scrollHeight}
                    isSelected={isShowingPreviousVote && Boolean(scrollEntry.isSelected)}
                    onSelectedAnswerChange={
                      _.includes(['photo-voting-vote', 'photo-submissions-submit'], ballotDisplayType)
                        ? (syntheticEvent: SyntheticEvent) => handleSelectedAnswerChange(syntheticEvent, scrollEntry)
                        : null
                    }
                    onOpenEntryImagesModal={openEntryImagesModal}
                  />
                }
                if (scrollEntry.type === 'ad') {
                  return <PhotoGalleryAd
                    key={scrollEntry.key}
                    variant='scroll'
                    ballot={ballot}
                    question={currentQuestion}
                    group={currentGroup}
                    displayAd={displayAd}
                  />
                }
              })}
              {/* TODO: What if this was a 'slowly scroll to beginning' button? */}
              {!shouldCenterEmptySubmissionWizard &&
                <div style={{
                  width: '100px',
                  backgroundColor: 'transparent',
                  flexShrink: '0',
                }} />
              }
            </div>
          </div>
        </div>
        {modalEntry &&
          <EntryImagesModal
            entry={{...modalEntry, cover_image: modalEntry.cover_image ?? undefined}}
            ballotAdditionalFormFields={ballot.photo_submission_settings?.additional_form_fields}
            currentWindowHeight={currentWindowHeight}
            currentWindowWidth={currentWindowWidth}
            hasVoter={Boolean(voter?._id)}
            onCastVote={voteObject => onCastVote({...voteObject, photoImage: voteObject.photoImage ?? '', photoName: voteObject.photoName ?? ''} as any)}
            onCloseEntryImagesModal={onCloseEntryImagesModal}
            ballotDisplayType={ballotDisplayType ?? undefined}
          />
        }
      </div>
    )
    : null
}

export default PhotoScrollGallery
